// =========================================================================
// Chart scales — pure domain→range helpers. Fork-owned (zone C).
//
// The demo draws every chart by hand into a fixed-`viewBox` SVG. This module
// is the arithmetic half of that: no React, no DOM, no tokens, no rounding
// beyond what a caller asks for. Keeping it separate is what lets a transform
// compute bar geometry in a test with no renderer, and what keeps the
// snapshot bytes identical between runs (plan §F).
//
// Transformation layer (layer 3) — pure functions, one concern.
// @see docs/foundations/4-layer-architecture.md
// =========================================================================

/** A closed numeric interval. */
export interface Span {
  readonly min: number;
  readonly max: number;
}

/**
 * Linear map from a value domain onto a pixel range.
 *
 * A zero-width domain maps everything to `range.min` rather than dividing by
 * zero — a single-valued series draws flat rather than blowing up.
 */
export function linearScale(domain: Span, range: Span): (value: number) => number {
  const dSpan = domain.max - domain.min;
  const rSpan = range.max - range.min;
  if (dSpan === 0) return () => range.min;
  return (value) => range.min + ((value - domain.min) / dSpan) * rSpan;
}

/**
 * The domain of a series, forced to include zero.
 *
 * Bar charts are read by area, so a bar axis that does not start at zero
 * misstates every comparison on it. An empty series returns `0…1` so the
 * caller still has a usable scale.
 */
export function zeroDomain(values: readonly number[]): Span {
  if (values.length === 0) return { min: 0, max: 1 };
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  return { min, max: max === min ? min + 1 : max };
}

/**
 * Width of one segment of a stack, as a percentage of the stack total.
 *
 * Returns `0` when the total is zero or negative rather than `NaN`, so a
 * site with no ledger rows renders an empty bar instead of a broken one.
 */
export function sharePct(part: number, total: number): number {
  if (!(total > 0)) return 0;
  return (part / total) * 100;
}

/**
 * Evenly spaced band positions across a range — the categorical axis.
 *
 * `padInner` is the share of each step given over to the gap between bands,
 * `0`…`1`. Returns one `{ start, size }` per band in input order.
 */
export function bandPositions(
  count: number,
  range: Span,
  padInner = 0.2,
): { start: number; size: number }[] {
  if (count <= 0) return [];
  const step = (range.max - range.min) / count;
  const size = step * (1 - padInner);
  const gap = (step - size) / 2;
  return Array.from({ length: count }, (_, i) => ({
    start: range.min + i * step + gap,
    size,
  }));
}

/**
 * "Nice" tick values across a zero-anchored domain.
 *
 * Picks a 1 / 2 / 5 × 10ⁿ step so the labels read as round numbers, then
 * emits every tick from zero to at or beyond the domain max. `count` is a
 * target, not a guarantee — rounding the step moves the actual count by one
 * either way, which is the point of a nice scale.
 */
export function niceTicks(domain: Span, count = 4): number[] {
  const span = domain.max - domain.min;
  if (!(span > 0) || count <= 0) return [domain.min];
  const rough = span / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalised = rough / magnitude;
  const step = (normalised >= 5 ? 10 : normalised >= 2 ? 5 : normalised >= 1 ? 2 : 1) * magnitude;
  const ticks: number[] = [];
  for (let t = Math.ceil(domain.min / step) * step; t <= domain.max + step * 0.001; t += step) {
    // Re-round each tick: repeated addition of a fractional step drifts.
    ticks.push(Number((Math.round(t / step) * step).toPrecision(12)));
  }
  return ticks;
}

/** Clamp into a closed interval. */
export const clamp = (value: number, min: number, max: number): number =>
  value < min ? min : value > max ? max : value;

/**
 * Polar point on a circle, with 12 o'clock as 0 and clockwise positive —
 * the reading direction of a gauge, not the mathematical convention.
 */
export function polar(
  cx: number,
  cy: number,
  radius: number,
  fraction: number,
): { x: number; y: number } {
  const angle = fraction * Math.PI * 2 - Math.PI / 2;
  return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
}
